import React, { Fragment, useEffect, useContext, useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Portal from 'utils/portal/Portal';
import { areas } from 'constants/areas';
import { withAreaGetter } from 'providers/AreasConsumers';
import { StyledTable } from 'components/common/Styles';
import { TableBody, TableCell, TableHead } from '@mui/material';
import { RoundedHeaderTableRow, ThemedHeaderTableCell } from 'components/common/table/TableStyles';
import { IconButton, Card } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import FlexBox from 'components/common/FlexBox';
import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { usePermission } from 'providers/RightsHooks';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { ServiceContext } from 'providers/ServiceProvider';
import { useApolloClient } from '@apollo/client';
import { removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import PartnerTypeExportComponent from './PartnerTypeExportComponent';
import PartnerTypeComponent from './PartnerTypeComponent';
import PartnerTypeDetailsComponent from '../../tabs/components/PartnerTypeDetailsComponent';
import { PARTNER_TYPES_QUERY } from '../queries';
import { ADD_UPDATE_PARTNER_TYPES, DELETE_PARTNER_TYPE } from '../mutations';
import { PARTNER_TYPES_SAVED, PARTNER_TYPE_DELETED } from '../subscriptions';
import { ADMIN_PARTNER_TYPES_WRITE } from 'constants/rightsCodes';
import { Prompt } from 'react-router-dom';
import { partnerTypesValidator } from '../../../validators';
import { autoScroll } from '../../../functions';

const PartnerTypesContainer = ({ getElemByAreaKey }) => {
  const [t] = useTranslation();
  const [activeService] = useContext(ServiceContext);
  const confirm = useConfirmDialog();
  const addToast = useToast();
  const client = useApolloClient();

  const canWrite = usePermission(ADMIN_PARTNER_TYPES_WRITE);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [validation, validate, resetValidation] = useDirtyFieldValidation(partnerTypesValidator);

  const [initialData, setInitialData] = useState([]);
  const [pending, setPending] = useState(false);
  const [initialBaseTypes, setInitialBaseType] = useState([]);

  const [selectedPartnerType, setSelectedPartnerType] = useState();
  const [selectedPartnerValidation, setSelectedPartnerValidation] = useState();
  const [partnerTypeIds, setPartnerTypeIds] = useState([]);
  const refs = { detailsArea: useRef({}) };

  const { loading } = useQuery(PARTNER_TYPES_QUERY, {
    onCompleted: data => {
      const partnerTypes = (data?.partnerTypes || []).map(x => ({
        ...x,
        editing: false,
        properties: x.properties.map(y => ({ ...y, key: crypto.randomUUID(), editing: false }))
      }));
      const initialBaseTypeSample =
        partnerTypes.length > 0
          ? partnerTypes[0].baseTypes.map(baseType => ({
              ...baseType,
              isSelected: false
            }))
          : [];
      setInitialBaseType(initialBaseTypeSample);
      setInitialData(partnerTypes);
      resetLens(partnerTypes);
    }
  });

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  // #region Mutations

  const [savePartnerTypes] = useMutation(ADD_UPDATE_PARTNER_TYPES, {
    onError: () => {
      setPending(false);
    }
  });

  const [deletePartnerType] = useMutation(DELETE_PARTNER_TYPE, {
    onError: () => {
      setPending(false);
    }
  });

  // #endregion

  const handleSave = useCallback(() => {
    let data = lens |> get;
    if (partnerTypeIds && partnerTypeIds.length !== 0) data = data.filter(x => partnerTypeIds.includes(x.id));
    if (!validate(data)) return;
    setPending(true);
    const sanitizedData = data.map(item => {
      // eslint-disable-next-line no-unused-vars
      const { editing, ...sanitizedItem } = item;
      sanitizedItem.baseTypes.filter(x => x.isSelected);
      if (typeof sanitizedItem.id === 'string') sanitizedItem.id = null;
      if (sanitizedItem.properties) {
        sanitizedItem.properties = sanitizedItem.properties.map(property => {
          // eslint-disable-next-line no-unused-vars
          const { propertyName, propertyTypeCode, key, editing, ...rest } = property;
          rest.defaultValue =
            property.defaultValue instanceof Date ? property.defaultValue.toLocaleDateString() : property.defaultValue?.toString() || '';
          rest.mandatory = !!property.mandatory;
          rest.partnerTypeId = sanitizedItem.id;
          return rest;
        });
      }
      return sanitizedItem;
    });
    savePartnerTypes({
      variables: { input: sanitizedData }
    });
  }, [partnerTypeIds, savePartnerTypes, lens, validate]);

  const handleDelete = useCallback(
    partnerTypeId => {
      confirm(
        () => {
          deletePartnerType({
            variables: { partnerTypeId },
            errorHandlingParams: { id: partnerTypeId }
          });
        },
        {
          title: t('PartnerType.DeletePartnerType'),
          content: t('PartnerType.AreYouSureDeletePartnerType')
        }
      );
    },
    [confirm, t, deletePartnerType]
  );

  const handleAdd = useCallback(() => {
    const items = lens |> get;
    const newPartnerType = {
      id: crypto.randomUUID(),
      name: null,
      code: null,
      editing: true,
      baseTypes: initialBaseTypes,
      properties: []
    };
    (lens |> set)([newPartnerType, ...items]);
  }, [lens, initialBaseTypes]);

  const handleEdit = useCallback(
    id => {
      const items = lens |> get;
      const updated = items.map(x => (x.id === id ? { ...x, editing: true } : x));
      (lens |> set)(updated);
    },
    [lens]
  );

  const handleUndoChanges = useCallback(
    id => {
      const items = lens |> get;
      const unmodifiedElement = initialData.find(el => el.id === id);
      const updated = items
        .map(x => {
          if (typeof id === 'string' && x.id === id) {
            return null;
          } else if (x.id === id) {
            return { ...x, editing: false, code: unmodifiedElement.code, name: unmodifiedElement.name };
          } else {
            return x;
          }
        })
        .filter(Boolean);
      (lens |> set)(updated);
    },
    [lens, initialData]
  );

  //#endregion

  // #region Subscriptions

  useSubscription(PARTNER_TYPES_SAVED, {
    onData: ({ client, data }) => {
      const currentPartnerTypes = lens |> get;
      const newPartnerTypes = data.data.partnerTypesSaved.partnerTypes.map(x => ({
        ...x,
        editing: false,
        properties: x.properties.map(y => ({ ...y, editing: false }))
      }));
      let updatedPartnerTypes = currentPartnerTypes
        .filter(x => typeof x.id !== 'string')
        .map(x =>
          newPartnerTypes.some(y => y.id === x.id)
            ? newPartnerTypes.find(y => y.id === x.id)
            : { ...x, properties: x.properties.map(z => ({ ...z, editing: false })) }
        );
      updatedPartnerTypes = [...updatedPartnerTypes, ...newPartnerTypes.filter(x => !updatedPartnerTypes.some(y => y.id === x.id))];
      setSelectedPartnerType(prevState => newPartnerTypes?.find(x => x.code === prevState?.code));
      client.writeQuery({
        query: PARTNER_TYPES_QUERY,
        context: { serviceName: activeService?.name },
        data: { partnerTypes: updatedPartnerTypes }
      });
      resetLens(updatedPartnerTypes);
      setInitialData(updatedPartnerTypes);
      resetValidation();
      setSelectedPartnerValidation();
      setPending(false);
      setPartnerTypeIds([]);
      addToast(t('PartnerType.PartnerTypeSaved'), 'success');
    }
  });

  useSubscription(PARTNER_TYPE_DELETED, {
    onData: ({
      data: {
        data: {
          partnerTypeDeleted: { id }
        }
      }
    }) => {
      const updatedPartnerTypes = (lens |> get).filter(x => !(x.id === id));
      resetLens(updatedPartnerTypes);
      setInitialData(updatedPartnerTypes);
      setSelectedPartnerType();
      removeCachedDetail(client, { name: 'partnerTypes', value: PARTNER_TYPES_QUERY }, {}, x => x.id === id, ['id']);
      addToast(t('PartnerType.PartnerTypeDeleted'), 'success');
    }
  });

  //#endregion

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  const disabled = useMemo(() => !canWrite || pending, [canWrite, pending]);

  const selectedPartnerLens = selectedPartnerType && (lens |> sequence).find(x => (x.id |> get) === selectedPartnerType.id);

  const markPartnerType = useCallback(id => {
    setPartnerTypeIds(prev => {
      if (!prev.includes(id)) return prev;
      return [...prev, id];
    });
  }, []);

  const handleChangeProperty = useCallback(
    partnerTypeId => (name, value, lens) => {
      markPartnerType(partnerTypeId);
      if (!name) return;
      (lens[name] |> set)(value);
    },
    [markPartnerType]
  );

  const handleDeleteProperty = (id, propertyId) => {
    markPartnerType(id);

    const items = lens |> get;
    const updated = items.map(x =>
      x.id === id ? { ...x, properties: x.properties.filter(y => !(y.partnerTypeId === id && y.propertyId === propertyId)) } : x
    );
    (lens |> set)(updated);
  };

  const navigateToDetailsArea = () => {
    autoScroll(refs.detailsArea);
  };

  return (
    <Fragment>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Portal container={getElemByAreaKey(areas.headerLinks)}>
        {canWrite && (
          <>
            <IconButton
              size='medium'
              type='add'
              variant='text'
              color='primary'
              tooltip={t('Add')}
              disabled={disabled}
              onClick={handleAdd}
            />
            <IconButton
              size='medium'
              type='save'
              variant='text'
              color='primary'
              tooltip={t('Save')}
              disabled={disabled || !isDirty}
              onClick={handleSave}
            />
          </>
        )}
      </Portal>
      <FlexBox direction='column' gap={2}>
        <Card>
          <FlexBox justifyContent='end' mb={1}>
            <PartnerTypeExportComponent />
          </FlexBox>
          <StyledTable>
            <TableHead>
              <RoundedHeaderTableRow>
                <ThemedHeaderTableCell>{t('PartnerType.Code')}</ThemedHeaderTableCell>
                <ThemedHeaderTableCell>{t('PartnerType.Name')}</ThemedHeaderTableCell>
                <ThemedHeaderTableCell align='right' width='120px'>
                  {t('Actions')}
                </ThemedHeaderTableCell>
              </RoundedHeaderTableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableCell colSpan={3} sx={{ px: 0.5 }}>
                  <WrappedFakeText />
                </TableCell>
              )}
              {!loading &&
                (lens.length |> get) > 0 &&
                (lens |> sequence).map((partnerTypeLens, index) => (
                  <PartnerTypeComponent
                    key={partnerTypeLens.id |> get}
                    lens={partnerTypeLens}
                    disabled={disabled}
                    index={index}
                    validation={validation[index]}
                    onSelectedPartner={setSelectedPartnerType}
                    onSelectedPartnerValidation={setSelectedPartnerValidation}
                    onEdit={handleEdit}
                    onChangeProperty={handleChangeProperty}
                    onDelete={handleDelete}
                    onCancelAdd={handleUndoChanges}
                    navigateToDetailsArea={navigateToDetailsArea}
                  />
                ))}
            </TableBody>
          </StyledTable>
        </Card>
        <div ref={refs.detailsArea}>
          {selectedPartnerType !== null && selectedPartnerType !== undefined && (
            <PartnerTypeDetailsComponent
              lens={selectedPartnerLens}
              disabled={disabled}
              initialData={initialData && initialData.find(x => x.id === selectedPartnerType?.id)}
              validation={validation[selectedPartnerValidation]}
              refName={refs.detailsArea}
              onChangeProperty={handleChangeProperty}
              onDeleteProperty={handleDeleteProperty}
            />
          )}
        </div>
      </FlexBox>
    </Fragment>
  );
};

PartnerTypesContainer.propTypes = {
  getElemByAreaKey: PropTypes.func
};

export default withAreaGetter(PartnerTypesContainer);
